import React from 'react';

import type { GetDiscernDocumentsInput } from 'generated/types';

export interface DiscernServerFilters {
  dateFrom: Date | null;
  dateTo: Date | null;
  patientName: string;
}

export interface DiscernClientFilters {
  uploader: string;
  status: string;
}

export interface UseDiscernFiltersReturn {
  serverFilters: DiscernServerFilters;
  clientFilters: DiscernClientFilters;
  setServerFilter: <K extends keyof DiscernServerFilters>(
    key: K,
    value: DiscernServerFilters[K]
  ) => void;
  setClientFilter: <K extends keyof DiscernClientFilters>(
    key: K,
    value: DiscernClientFilters[K]
  ) => void;
  clearAllFilters: () => void;
  toQueryInput: (
    base?: Partial<GetDiscernDocumentsInput>
  ) => GetDiscernDocumentsInput;
  hasActiveFilters: boolean;
}

const DEFAULT_SERVER_FILTERS: DiscernServerFilters = {
  dateFrom: null,
  dateTo: null,
  patientName: '',
};

const DEFAULT_CLIENT_FILTERS: DiscernClientFilters = {
  uploader: '',
  status: '',
};

export function useDiscernFilters(): UseDiscernFiltersReturn {
  const [serverFilters, setServerFilters] =
    React.useState<DiscernServerFilters>(DEFAULT_SERVER_FILTERS);
  const [clientFilters, setClientFilters] =
    React.useState<DiscernClientFilters>(DEFAULT_CLIENT_FILTERS);

  const setServerFilter = React.useCallback(
    <K extends keyof DiscernServerFilters>(
      key: K,
      value: DiscernServerFilters[K]
    ) => {
      setServerFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const setClientFilter = React.useCallback(
    <K extends keyof DiscernClientFilters>(
      key: K,
      value: DiscernClientFilters[K]
    ) => {
      setClientFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const clearAllFilters = React.useCallback(() => {
    setServerFilters(DEFAULT_SERVER_FILTERS);
    setClientFilters(DEFAULT_CLIENT_FILTERS);
  }, []);

  const toQueryInput = React.useCallback(
    (base?: Partial<GetDiscernDocumentsInput>): GetDiscernDocumentsInput => {
      const input: GetDiscernDocumentsInput = { ...base };
      if (serverFilters.dateFrom)
        input.date_from = serverFilters.dateFrom.toISOString();
      if (serverFilters.dateTo)
        input.date_to = serverFilters.dateTo.toISOString();
      if (serverFilters.patientName)
        input.patient_name = serverFilters.patientName;
      return input;
    },
    [serverFilters]
  );

  const hasActiveFilters =
    !!serverFilters.dateFrom ||
    !!serverFilters.dateTo ||
    !!serverFilters.patientName ||
    !!clientFilters.uploader ||
    clientFilters.status !== '';

  return {
    serverFilters,
    clientFilters,
    setServerFilter,
    setClientFilter,
    clearAllFilters,
    toQueryInput,
    hasActiveFilters,
  };
}
